export type LinkStatus = 'saved' | 'draft' | 'suggestion' | 'removing';

export type ItemDirection = 'in' | 'out';

export type ItemPurpose = 'invoice' | 'partner' | 'owner_refund' | 'partner_refund';

export type ColumnMode = 'invoices' | 'partners' | 'refunds' | 'partner_refund';

export type ItemFilter = 'all' | 'unmatched' | 'linked';

export interface Owner {
  id: string;
  name: string;
  unit: string;
  prepayment: number;
}

export interface Invoice {
  id: string;
  number: string;
  ownerId?: string;
  ownerName: string;
  unit?: string;
  period?: string;
  note?: 'partial' | 'new' | 'rent' | 'storno' | 'paid';
  amount: number;
  paid: number;
  status: 'open' | 'partial' | 'paid' | 'storno';
  priority?: number;
}

export interface Partner {
  id: string;
  name: string;
  note: string;
  paid: number;
}

export interface Firm {
  id: string;
  name: string;
}

export interface StatementItem {
  id: string;
  direction: ItemDirection;
  purpose: ItemPurpose;
  name: string;
  unit?: string;
  subtitle: 'no_reference' | 'missing_reference' | 'building_payment' | 'refund' | 'storno_reference' | 'paid_reference';
  reference?: string;
  amount: number;
  ownerId?: string;
  partnerId?: string;
}

export interface PostingLink {
  id: string;
  sourceId: string;
  targetType: 'invoice' | 'prepayment' | 'partner';
  targetId: string;
  ownerId?: string;
  amount: number;
  status: LinkStatus;
  effect: 'increase' | 'decrease';
}

export interface Workspace {
  id: string;
  statementNumber: string;
  statementDate: string;
  accountName: string;
  buildingName: string;
  owners: Owner[];
  invoices: Invoice[];
  partners: Partner[];
  firms: Firm[];
  items: StatementItem[];
  links: PostingLink[];
}

export interface StatementSummary {
  id: string;
  statement_number: string;
  statement_date: string;
  account_name: string;
  building_name: string;
  linked: number;
  total: number;
  status: 'open' | 'partial' | 'done';
}

export type ConnectResult =
  | { type: 'ok'; workspace: Workspace }
  | { type: 'need-owner'; amount: number }
  | { type: 'error'; reason: string };

const money = (value: number) => Math.round(value * 100) / 100;

export const formatAmount = (value: number) => {
  const sign = value < 0 ? '-' : '';
  const digits = Math.abs(Math.round(value)).toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.');

  return sign + digits;
};

export const cloneWorkspace = (workspace: Workspace): Workspace => JSON.parse(JSON.stringify(workspace));

export const isActive = (status: LinkStatus) => status === 'saved' || status === 'draft';

export const columnMode = (item: StatementItem): ColumnMode => {
  if (item.purpose === 'partner') {
    return 'partners';
  }

  if (item.purpose === 'owner_refund') {
    return 'refunds';
  }

  if (item.purpose === 'partner_refund') {
    return 'partner_refund';
  }

  return 'invoices';
};

export const itemLinks = (links: PostingLink[], sourceId: string) => links.filter((link) => link.sourceId === sourceId);

export const allocated = (links: PostingLink[], sourceId: string) => itemLinks(links, sourceId)
  .filter((link) => isActive(link.status))
  .reduce((sum, link) => sum + link.amount, 0);

export const remaining = (item: StatementItem, links: PostingLink[]) => money(Math.max(0, item.amount - allocated(links, item.id)));

export const invoiceOpen = (invoice: Invoice, links: PostingLink[]) => {
  const used = links
    .filter((link) => link.targetType === 'invoice' && link.targetId === invoice.id && isActive(link.status))
    .reduce((sum, link) => sum + link.amount, 0);

  return money(Math.max(0, invoice.amount - invoice.paid - used));
};

export const ownerBalance = (owner: Owner, links: PostingLink[]) => {
  const delta = links
    .filter((link) => link.targetType === 'prepayment' && link.ownerId === owner.id && isActive(link.status))
    .reduce((sum, link) => sum + (link.effect === 'decrease' ? -link.amount : link.amount), 0);

  return money(Math.max(0, owner.prepayment + delta));
};

export const partnerBalance = (partner: Partner, links: PostingLink[]) => {
  const delta = links
    .filter((link) => link.targetType === 'partner' && link.targetId === partner.id && isActive(link.status))
    .reduce((sum, link) => sum + (link.effect === 'decrease' ? -link.amount : link.amount), 0);

  return money(Math.max(0, partner.paid + delta));
};

export const suggestionCount = (links: PostingLink[], sourceId: string) => itemLinks(links, sourceId)
  .filter((link) => link.status === 'suggestion').length;

export const activeLinkCount = (links: PostingLink[], sourceId: string) => itemLinks(links, sourceId)
  .filter((link) => link.status !== 'suggestion').length;

const hasUnsaved = (links: PostingLink[], sourceId?: string) => links.some((link) => (
  (!sourceId || link.sourceId === sourceId)
  && (link.status === 'draft' || link.status === 'removing')
));

export const isItemSettled = (item: StatementItem, links: PostingLink[]) => (
  remaining(item, links) === 0 && !hasUnsaved(links, item.id) && suggestionCount(links, item.id) === 0
);

export const visibleItems = (workspace: Workspace, filter: ItemFilter) => workspace.items.filter((item) => {
  if (filter === 'all') {
    return true;
  }

  const settled = isItemSettled(item, workspace.links);

  if (filter === 'linked') {
    return settled;
  }

  return !settled;
});

export const settledCount = (workspace: Workspace) => workspace.items
  .filter((item) => remaining(item, workspace.links) === 0).length;

export const isFinished = (workspace: Workspace) => workspace.items.every((item) => isItemSettled(item, workspace.links));

export const postingStatus = (workspace: Workspace): StatementSummary['status'] => {
  const posted = workspace.items.filter((item) => isItemSettled(item, workspace.links)).length;

  if (workspace.items.length > 0 && posted === workspace.items.length) {
    return 'done';
  }

  if (posted === 0) {
    return 'open';
  }

  return 'partial';
};

export const summarize = (workspace: Workspace): StatementSummary => ({
  id: workspace.id,
  statement_number: workspace.statementNumber,
  statement_date: workspace.statementDate,
  account_name: workspace.accountName,
  building_name: workspace.buildingName,
  linked: settledCount(workspace),
  total: workspace.items.length,
  status: postingStatus(workspace),
});

export const commitLinks = (workspace: Workspace): Workspace => ({
  ...workspace,
  links: workspace.links
    .filter((link) => link.status !== 'removing')
    .map((link) => (link.status === 'draft' ? { ...link, status: 'saved' } : link)),
});

const ownerFromLinks = (workspace: Workspace, item: StatementItem) => {
  if (item.ownerId) {
    return item.ownerId;
  }

  const linked = workspace.links.find((link) => (
    link.sourceId === item.id && link.targetType === 'invoice' && isActive(link.status)
  ));

  if (!linked) {
    return null;
  }

  return workspace.invoices.find((invoice) => invoice.id === linked.targetId)?.ownerId || null;
};

const pairExists = (workspace: Workspace, itemId: string, targetType: PostingLink['targetType'], targetId: string) => (
  workspace.links.some((link) => (
    link.sourceId === itemId
    && link.targetType === targetType
    && link.targetId === targetId
    && link.status !== 'removing'
  ))
);

const nextId = () => `link-${Date.now()}-${Math.round(Math.random() * 10000)}`;

export const connectNodes = (workspace: Workspace, sourceNodeId: string, targetNodeId: string): ConnectResult => {
  const item = workspace.items.find((entry) => `item-${entry.id}` === sourceNodeId);

  if (!item) {
    return { type: 'error', reason: 'wrong_target' };
  }

  const left = remaining(item, workspace.links);

  if (targetNodeId.startsWith('prepay-')) {
    return { type: 'error', reason: 'duplicate' };
  }

  if (targetNodeId === 'zone-prepay') {
    if (item.purpose !== 'invoice') {
      return { type: 'error', reason: 'wrong_target' };
    }

    if (left <= 0) {
      return { type: 'error', reason: 'no_remaining' };
    }

    const ownerId = ownerFromLinks(workspace, item);

    if (!ownerId) {
      return { type: 'need-owner', amount: left };
    }

    if (pairExists(workspace, item.id, 'prepayment', `prepay-${item.id}-${ownerId}`)) {
      return { type: 'error', reason: 'duplicate' };
    }

    return {
      type: 'ok',
      workspace: addLink(workspace, {
        id: nextId(),
        sourceId: item.id,
        targetType: 'prepayment',
        targetId: `prepay-${item.id}-${ownerId}`,
        ownerId,
        amount: left,
        status: 'saved',
        effect: 'increase',
      }),
    };
  }

  if (targetNodeId.startsWith('invoice-')) {
    if (item.purpose !== 'invoice') {
      return { type: 'error', reason: 'wrong_target' };
    }

    const invoice = workspace.invoices.find((entry) => `invoice-${entry.id}` === targetNodeId);

    if (!invoice) {
      return { type: 'error', reason: 'wrong_target' };
    }

    if (invoice.status === 'storno') {
      return { type: 'error', reason: 'storno' };
    }

    const existing = workspace.links.find((link) => (
      link.sourceId === item.id && link.targetType === 'invoice' && link.targetId === invoice.id && link.status !== 'removing'
    ));

    if (existing?.status === 'suggestion') {
      return {
        type: 'ok',
        workspace: {
          ...workspace,
          links: workspace.links.map((link) => (link.id === existing.id ? { ...link, status: 'saved' } : link)),
        },
      };
    }

    if (existing) {
      return { type: 'error', reason: 'duplicate' };
    }

    if (left <= 0) {
      return { type: 'error', reason: 'no_remaining' };
    }

    const open = invoiceOpen(invoice, workspace.links);

    if (open <= 0) {
      return { type: 'error', reason: 'invoice_closed' };
    }

    return {
      type: 'ok',
      workspace: addLink(workspace, {
        id: nextId(),
        sourceId: item.id,
        targetType: 'invoice',
        targetId: invoice.id,
        amount: money(Math.min(left, open)),
        status: 'saved',
        effect: 'increase',
      }),
    };
  }

  if (targetNodeId.startsWith('partner-')) {
    if (item.purpose !== 'partner' && item.purpose !== 'partner_refund') {
      return { type: 'error', reason: 'wrong_target' };
    }

    const partner = workspace.partners.find((entry) => `partner-${entry.id}` === targetNodeId);

    if (!partner) {
      return { type: 'error', reason: 'wrong_target' };
    }

    if (pairExists(workspace, item.id, 'partner', partner.id)) {
      return { type: 'error', reason: 'duplicate' };
    }

    if (left <= 0) {
      return { type: 'error', reason: 'no_remaining' };
    }

    if (item.purpose === 'partner_refund') {
      const paid = partnerBalance(partner, workspace.links);

      if (paid <= 0) {
        return { type: 'error', reason: 'partner_refund_exceeds' };
      }

      return {
        type: 'ok',
        workspace: addLink(workspace, {
          id: nextId(),
          sourceId: item.id,
          targetType: 'partner',
          targetId: partner.id,
          amount: money(Math.min(left, paid)),
          status: 'saved',
          effect: 'decrease',
        }),
      };
    }

    return {
      type: 'ok',
      workspace: addLink(workspace, {
        id: nextId(),
        sourceId: item.id,
        targetType: 'partner',
        targetId: partner.id,
        amount: left,
        status: 'saved',
        effect: 'increase',
      }),
    };
  }

  if (targetNodeId.startsWith('owner-')) {
    if (item.purpose !== 'owner_refund') {
      return { type: 'error', reason: 'wrong_target' };
    }

    const owner = workspace.owners.find((entry) => `owner-${entry.id}` === targetNodeId);

    if (!owner) {
      return { type: 'error', reason: 'wrong_target' };
    }

    const balance = ownerBalance(owner, workspace.links);

    if (left <= 0) {
      return { type: 'error', reason: 'no_remaining' };
    }

    if (balance <= 0) {
      return { type: 'error', reason: 'refund_exceeds' };
    }

    if (pairExists(workspace, item.id, 'prepayment', `refund-${item.id}-${owner.id}`)) {
      return { type: 'error', reason: 'duplicate' };
    }

    return {
      type: 'ok',
      workspace: addLink(workspace, {
        id: nextId(),
        sourceId: item.id,
        targetType: 'prepayment',
        targetId: `refund-${item.id}-${owner.id}`,
        ownerId: owner.id,
        amount: money(Math.min(left, balance)),
        status: 'saved',
        effect: 'decrease',
      }),
    };
  }

  return { type: 'error', reason: 'wrong_target' };
};

export const connectPrepayment = (workspace: Workspace, itemId: string, ownerId: string, amount: number): ConnectResult => {
  const item = workspace.items.find((entry) => entry.id === itemId);

  if (!item || amount <= 0) {
    return { type: 'error', reason: 'no_remaining' };
  }

  return {
    type: 'ok',
    workspace: addLink(workspace, {
      id: nextId(),
      sourceId: item.id,
      targetType: 'prepayment',
      targetId: `prepay-${item.id}-${ownerId}`,
      ownerId,
      amount,
      status: 'saved',
      effect: 'increase',
    }),
  };
};

const addLink = (workspace: Workspace, link: PostingLink): Workspace => ({
  ...workspace,
  links: [...workspace.links, link],
});

export const cycleLink = (workspace: Workspace, linkId: string): Workspace => {
  const link = workspace.links.find((entry) => entry.id === linkId);

  if (!link) {
    return workspace;
  }

  if (link.status === 'suggestion') {
    return {
      ...workspace,
      links: workspace.links.map((entry) => (entry.id === linkId ? { ...entry, status: 'saved' } : entry)),
    };
  }

  return {
    ...workspace,
    links: workspace.links.filter((entry) => entry.id !== linkId),
  };
};

const invoiceNumber = (workspace: Workspace, id: string) => (
  workspace.invoices.find((entry) => entry.id === id)?.number || id
);

const ownerLabel = (workspace: Workspace, ownerId?: string) => (
  workspace.owners.find((entry) => entry.id === ownerId)?.name || ''
);

const partnerNameOf = (workspace: Workspace, id: string) => (
  workspace.partners.find((entry) => entry.id === id)?.name || id
);

const questionFor = (
  workspace: Workspace,
  link: PostingLink,
  kind: 'link' | 'suggestion' | 'unlink',
  t: (key: string, options?: any) => string,
) => {
  const amount = formatAmount(link.amount);

  if (link.targetType === 'invoice') {
    const number = invoiceNumber(workspace, link.targetId);

    if (kind === 'suggestion') {
      return t('posting.confirm_suggestion', { amount, number });
    }

    if (kind === 'unlink') {
      return t('posting.confirm_unlink_invoice', { amount, number });
    }

    return t('posting.confirm_link_invoice', { amount, number });
  }

  if (link.targetType === 'prepayment') {
    const name = ownerLabel(workspace, link.ownerId);

    if (link.effect === 'decrease') {
      if (kind === 'unlink') {
        return t('posting.confirm_unlink_refund', { amount, name });
      }

      return t('posting.confirm_link_refund', { amount, name });
    }

    if (kind === 'unlink') {
      return t('posting.confirm_unlink_prepay', { amount, name });
    }

    return t('posting.confirm_link_prepay', { amount, name });
  }

  const name = partnerNameOf(workspace, link.targetId);

  if (link.effect === 'decrease') {
    if (kind === 'unlink') {
      return t('posting.confirm_unlink_partner_refund', { amount, name });
    }

    return t('posting.confirm_link_partner_refund', { amount, name });
  }

  if (kind === 'unlink') {
    return t('posting.confirm_unlink_partner', { amount, name });
  }

  return t('posting.confirm_link_partner', { amount, name });
};

export const changeQuestion = (
  before: Workspace,
  after: Workspace,
  t: (key: string, options?: any) => string,
) => {
  const previous = new Map(before.links.map((link) => [link.id, link]));
  const nextIds = new Set(after.links.map((link) => link.id));
  const added = after.links.find((link) => !previous.has(link.id));

  if (added) {
    return questionFor(after, added, 'link', t);
  }

  const confirmed = after.links.find((link) => previous.get(link.id)?.status === 'suggestion' && link.status === 'saved');

  if (confirmed) {
    return questionFor(after, confirmed, 'suggestion', t);
  }

  const removed = before.links.find((link) => !nextIds.has(link.id));

  if (removed) {
    return questionFor(before, removed, 'unlink', t);
  }

  const partner = after.partners.find((entry) => !before.partners.some((item) => item.id === entry.id));

  if (partner) {
    return t('posting.confirm_add_partner', { name: partner.name });
  }

  return t('posting.confirm_generic');
};

export const addPartner = (workspace: Workspace, partner: Partner): Workspace => ({
  ...workspace,
  partners: workspace.partners.some((entry) => entry.id === partner.id)
    ? workspace.partners
    : [...workspace.partners, partner],
});

const owners = (): Owner[] => ([
  { id: 'sonja', name: 'Sonja Savić', unit: 'Stan 10', prepayment: 0 },
  { id: 'marija', name: 'Marija Marković', unit: 'Stan 2', prepayment: 0 },
  { id: 'milan', name: 'Milan Milić', unit: 'Stan 5', prepayment: 0 },
  { id: 'jovan', name: 'Jovan Jović', unit: 'Stan 3', prepayment: 0 },
  { id: 'ivana', name: 'Ivana Ilić', unit: 'Stan 8', prepayment: 0 },
  { id: 'jelena', name: 'Jelena Janković', unit: 'Stan 6', prepayment: 0 },
  { id: 'petar', name: 'Petar Petrović', unit: 'Stan 1', prepayment: 4800 },
  { id: 'ana', name: 'Ana Anić', unit: 'Stan 4', prepayment: 0 },
  { id: 'nikola', name: 'Nikola Nikolić', unit: 'Stan 7', prepayment: 0 },
  { id: 'dragan', name: 'Dragan Dražić', unit: 'Stan 9', prepayment: 0 },
]);

const invoice = (
  id: string,
  number: string,
  owner: Owner | undefined,
  ownerName: string,
  period: string,
  amount: number,
  extra: Partial<Invoice> = {},
): Invoice => ({
  id,
  number,
  ownerId: owner?.id,
  ownerName,
  unit: owner?.unit,
  period,
  amount,
  paid: 0,
  status: 'open',
  ...extra,
});

export const statement41 = (): Workspace => {
  const people = owners();
  const byId = (id: string) => people.find((owner) => owner.id === id);

  return {
    id: '41',
    statementNumber: '41',
    statementDate: '2026-10-12',
    accountName: 'tekući račun zgrade',
    buildingName: 'Brijanova 2',
    owners: people,
    firms: [
      { id: 'elektro', name: 'Elektroservis d.o.o.' },
      { id: 'voda', name: 'Vodoinstalater d.o.o.' },
    ],
    partners: [
      { id: 'moler', name: 'Moler d.o.o.', note: 'krečenje', paid: 30000 },
      { id: 'lift', name: 'Lift servis d.o.o.', note: 'liftovi', paid: 0 },
      { id: 'banka', name: 'Banka', note: 'provizije', paid: 0 },
    ],
    invoices: [
      invoice('inv-0102', '0102', byId('marija'), 'Marija', '10/2026', 4800),
      invoice('inv-0092', '0092', byId('marija'), 'Marija', '09/2026', 4300),
      invoice('inv-0105', '0105', byId('milan'), 'Milan', '10/2026', 4800),
      invoice('inv-0100', '0100', byId('sonja'), 'Sonja', '09/2026', 4300),
      invoice('inv-0110', '0110', byId('sonja'), 'Sonja', '10/2026', 4800),
      invoice('inv-0106', '0106', byId('jelena'), 'Jelena', '10/2026', 4800),
      invoice('inv-0103', '0103', byId('jovan'), 'Jovan', '10/2026', 4800, { paid: 3000, status: 'partial', note: 'partial', priority: 1 }),
      invoice('inv-0108', '0108', byId('ivana'), 'Ivana', '10/2026', 4800, { priority: 2 }),
      invoice('inv-0104', '0104', byId('ana'), 'Ana', '10/2026', 4800),
      invoice('inv-0107', '0107', byId('nikola'), 'Nikola', '10/2026', 4800),
      invoice('inv-0109', '0109', byId('dragan'), 'Dragan', '10/2026', 4800),
      invoice('inv-0131', '0131', byId('ivana'), 'Ivana', '10/2026', 4800, { note: 'new' }),
      invoice('inv-0098', '0098', byId('ivana'), 'Ivana', '09/2026', 4800, { status: 'storno', note: 'storno', priority: 50 }),
      invoice('inv-0101', '0101', byId('petar'), 'Petar', '10/2026', 4800, { paid: 4800, status: 'paid', note: 'paid', priority: 60 }),
      invoice('inv-0090', '0090', undefined, 'Reklame d.o.o.', '', 12000, { note: 'rent', priority: 20 }),
    ],
    items: [
      { id: 'sonja', direction: 'in', purpose: 'invoice', name: 'Sonja', unit: 'Stan 10', subtitle: 'no_reference', amount: 9100, ownerId: 'sonja' },
      { id: 'marija', direction: 'in', purpose: 'invoice', name: 'Marija', unit: 'Stan 2', subtitle: 'missing_reference', reference: '9999', amount: 10000, ownerId: 'marija' },
      { id: 'milan', direction: 'in', purpose: 'invoice', name: 'Milan', unit: 'Stan 5', subtitle: 'no_reference', amount: 4800, ownerId: 'milan' },
      { id: 'moler', direction: 'out', purpose: 'partner', name: 'Moler d.o.o.', subtitle: 'building_payment', amount: 30000, partnerId: 'moler' },
      { id: 'jelena', direction: 'in', purpose: 'invoice', name: 'Jelena', unit: 'Stan 6', subtitle: 'missing_reference', reference: '9999', amount: 4800, ownerId: 'jelena' },
      { id: 'unknown', direction: 'in', purpose: 'invoice', name: 'nepoznat uplatilac', subtitle: 'no_reference', amount: 5000 },
      { id: 'petar-out', direction: 'out', purpose: 'owner_refund', name: 'Petar Petrović', subtitle: 'refund', amount: 4800, ownerId: 'petar' },
      { id: 'moler-in', direction: 'in', purpose: 'partner_refund', name: 'Moler d.o.o.', subtitle: 'refund', amount: 2000, partnerId: 'moler' },
    ],
    links: [
      { id: 'l-sonja-1', sourceId: 'sonja', targetType: 'invoice', targetId: 'inv-0100', amount: 4300, status: 'saved', effect: 'increase' },
      { id: 'l-sonja-2', sourceId: 'sonja', targetType: 'invoice', targetId: 'inv-0110', amount: 4800, status: 'saved', effect: 'increase' },
      { id: 'l-marija-1', sourceId: 'marija', targetType: 'invoice', targetId: 'inv-0102', amount: 4800, status: 'saved', effect: 'increase' },
      { id: 'l-marija-2', sourceId: 'marija', targetType: 'prepayment', targetId: 'prepay-marija-marija', ownerId: 'marija', amount: 5200, status: 'saved', effect: 'increase' },
      { id: 'l-milan-1', sourceId: 'milan', targetType: 'invoice', targetId: 'inv-0105', amount: 4800, status: 'suggestion', effect: 'increase' },
    ],
  };
};

export const statement40 = (): Workspace => {
  const people = owners();
  const petar = people.find((owner) => owner.id === 'petar');
  const ana = people.find((owner) => owner.id === 'ana');

  return {
    id: '40',
    statementNumber: '40',
    statementDate: '2026-10-11',
    accountName: 'tekući račun zgrade',
    buildingName: 'Brijanova 2',
    owners: people,
    firms: [],
    partners: [],
    invoices: [
      invoice('inv40-petar', '0095', petar, 'Petar', '10/2026', 4800),
      invoice('inv40-ana', '0096', ana, 'Ana', '10/2026', 4800),
    ],
    items: [
      { id: 'petar', direction: 'in', purpose: 'invoice', name: 'Petar', unit: 'Stan 1', subtitle: 'no_reference', amount: 4800, ownerId: 'petar' },
      { id: 'ana', direction: 'in', purpose: 'invoice', name: 'Ana', unit: 'Stan 4', subtitle: 'no_reference', amount: 4800, ownerId: 'ana' },
    ],
    links: [
      { id: 'l40-1', sourceId: 'petar', targetType: 'invoice', targetId: 'inv40-petar', amount: 4800, status: 'saved', effect: 'increase' },
      { id: 'l40-2', sourceId: 'ana', targetType: 'invoice', targetId: 'inv40-ana', amount: 4800, status: 'saved', effect: 'increase' },
    ],
  };
};

export const sampleWorkspaces = () => [statement41(), statement40()];

export const findWorkspace = (
  workspaces: Record<string, Workspace>,
  row: { id?: string | number, statement_number?: string | number },
) => {
  const id = String(row.id ?? '');

  if (id && workspaces[id]) {
    return workspaces[id];
  }

  const number = String(row.statement_number ?? '');

  return Object.values(workspaces).find((workspace) => workspace.statementNumber === number) || null;
};

export const workspaceFromStatement = (row: any): Workspace => {
  const catalog = statement41();
  const id = String(row.id ?? row.statement_number ?? '');

  return {
    ...catalog,
    id,
    statementNumber: String(row.statement_number || id),
    statementDate: String(row.statement_date || catalog.statementDate).slice(0, 10),
    accountName: row.account_number || row.account_name || catalog.accountName,
    buildingName: row.building_name || catalog.buildingName,
    links: [],
    items: (row.items || []).map((item: any, index: number) => {
      const outflow = Number(item.outflow || 0);
      const inflow = Number(item.inflow || 0);
      const direction: ItemDirection = item.direction === 'out' || (outflow > 0 && inflow === 0) ? 'out' : 'in';
      const amount = Number(item.amount || (direction === 'out' ? outflow : inflow) || 0);

      return {
        id: `row-${index}`,
        direction,
        purpose: direction === 'out' ? 'partner' : 'invoice',
        name: item.partner_name || item.description || item.name || '',
        subtitle: item.reference_number || item.reference ? 'no_reference' : 'missing_reference',
        reference: item.reference_number || item.reference || '',
        amount,
      } as StatementItem;
    }),
  };
};

export const statementRowFromWorkspace = (workspace: Workspace) => ({
  id: workspace.id,
  statement_number: workspace.statementNumber,
  bank_name: workspace.buildingName,
  account_number: workspace.accountName,
  statement_date: workspace.statementDate,
  previous_balance: 0,
  building_name: workspace.buildingName,
  postingOnly: true,
  items: workspace.items.map((item) => ({
    direction: item.direction,
    amount: item.amount,
    inflow: item.direction === 'in' ? item.amount : 0,
    outflow: item.direction === 'out' ? item.amount : 0,
    description: item.name,
    reference_number: item.reference || '',
  })),
});
